import React, { useEffect, useState, type FC } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, useNavigate, Navigate } from 'react-router-dom';
import { Layout, Menu, Button, Space, Result, Spin } from 'antd';
import { CalendarOutlined, SettingOutlined, LoginOutlined, LogoutOutlined, DashboardOutlined, FundProjectionScreenOutlined } from '@ant-design/icons';
import Dashboard from './pages/Dashboard';
import ProjectSchedule from './pages/ProjectSchedule';
import MilestoneConfig from './pages/MilestoneConfig';
import CalendarConfig from './pages/CalendarConfig';
import OpportunityPool from './pages/OpportunityPool';
import ThreeYearPlan from './pages/ThreeYearPlan';
import Callback from './pages/Callback';
import { authService } from './api/auth';
import { OIDC_CONFIG } from './auth/oidcManager';
import { ensureIpcStatusEnumsLoaded } from './utils/ipcStatusEnums';
import { getModuleLastPath, rememberModulePath, resolveMenuNavigatePath } from './utils/moduleLastPath';
import './App.css';

const { Header, Content } = Layout;

const basename = (import.meta as any).env?.BASE_URL || '/';
const defaultRoute = basename.startsWith('/REQ-022/') ? '/config/templates' : '/dashboard/overview';
const isCallbackPath = (pathname: string) => pathname.toLowerCase() === '/callback';

const AuthGate: FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  const isAuthenticated = authService.isAuthenticated();

  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isCallbackPath(location.pathname)) return;
    if (OIDC_CONFIG.skipSSO) return;
    if (isAuthenticated) return;

    let cancelled = false;
    (async () => {
      try {
        await authService.initiateLogin({ returnTo: `${location.pathname}${location.search}` });
      } catch (e) {
        if (cancelled) return;
        const msg = e instanceof Error ? e.message : '发起单点登录失败';
        console.error('SSO initiateLogin error:', e);
        setError(msg);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, location.pathname, location.search]);

  if (!isCallbackPath(location.pathname) && !OIDC_CONFIG.skipSSO && !isAuthenticated) {
    if (error) {
      return (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
          <Result
            status="error"
            title="无法跳转到单点登录"
            subTitle={error}
            extra={
              <div style={{ fontSize: 12, color: '#666', maxWidth: 520 }}>
                <div>请检查：</div>
                <div>1) `.env` 是否配置了 `VITE_OIDC_AUTHORITY`</div>
                <div>2) IdP 是否已白名单回调 `https://ipctest.shjinjia.com.cn/callback`</div>
              </div>
            }
          />
        </div>
      );
    }
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <Spin size="large" tip="正在跳转单点登录..." />
      </div>
    );
  }

  return <>{children}</>;
};

const AppLayout: FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const selectedKey =
    location.pathname === '/'
      ? '/'
      : location.pathname.startsWith('/dashboard')
        ? '/dashboard/overview'
        : location.pathname.startsWith('/schedule')
          ? '/schedule'
          : location.pathname.startsWith('/opportunity')
            ? '/opportunity'
            : location.pathname.startsWith('/planning')
              ? '/planning'
              : location.pathname.startsWith('/config')
                ? '/config/templates'
                : location.pathname.startsWith('/calendar-config')
                  ? '/calendar-config/settings'
                  : location.pathname;
  const isAuthenticated = authService.isAuthenticated();
  const userInfo = authService.getAuthData()?.userInfo;

  useEffect(() => {
    if (!isAuthenticated && !OIDC_CONFIG.skipSSO) return;
    void ensureIpcStatusEnumsLoaded();
  }, [isAuthenticated]);

  useEffect(() => {
    rememberModulePath(location.pathname);
  }, [location.pathname]);

  const menuItems = [
    { key: '/dashboard/overview', icon: <DashboardOutlined />, label: '管理看板' },
    {
      key: 'opportunity-planning',
      icon: <FundProjectionScreenOutlined />,
      label: '机会与规划',
      children: [
        { key: '/opportunity', label: '机会池' },
        { key: '/planning', label: '三年规划' },
      ],
    },
    { key: '/schedule', icon: <CalendarOutlined />, label: '项目排期' },
    { key: '/config/templates', icon: <SettingOutlined />, label: '里程碑配置' },
    { key: '/calendar-config/settings', icon: <SettingOutlined />, label: '设置' },
  ];

  const handleLogin = () => {
    void authService.initiateLogin({ returnTo: `${location.pathname}${location.search}` });
  };

  const handleLogout = async () => {
    await authService.logout();
  };

  return (
    <Layout className="app-layout">
      <Header className="app-header">
        <div className="app-brand">化妆品开发排期工具</div>
        <Menu
          theme="dark"
          mode="horizontal"
          selectedKeys={[selectedKey]}
          items={menuItems}
          onClick={({ key }) => {
            if (key === 'opportunity-planning') return;
            navigate(resolveMenuNavigatePath(key));
          }}
          style={{ flex: 1, minWidth: 0 }}
        />
        <Space>
          {isAuthenticated ? (
            <>
              <span style={{ color: '#fff', marginRight: 8 }}>
                {userInfo?.name || userInfo?.email || '用户'}
              </span>
              <Button 
                type="text" 
                icon={<LogoutOutlined />} 
                onClick={handleLogout}
                style={{ color: '#fff' }}
              >
                登出
              </Button>
            </>
          ) : (
            <Button 
              type="text" 
              icon={<LoginOutlined />} 
              onClick={handleLogin}
              style={{ color: '#fff' }}
            >
              登录
            </Button>
          )}
        </Space>
      </Header>
      <Content className="app-content">
        <div className="app-content-inner">
          <Routes>
            <Route path="/" element={<Navigate to={defaultRoute} replace />} />
            <Route path="/dashboard" element={<Navigate to={getModuleLastPath('dashboard')} replace />} />
            <Route path="/dashboard/:tab" element={<Dashboard />} />
            <Route path="/opportunity" element={<OpportunityPool />} />
            <Route path="/planning" element={<ThreeYearPlan />} />
            <Route path="/planning/:planId" element={<ThreeYearPlan />} />
            <Route path="/schedule" element={<ProjectSchedule />} />
            <Route path="/schedule/:projectId" element={<ProjectSchedule />} />
            <Route path="/config" element={<Navigate to={getModuleLastPath('config')} replace />} />
            <Route path="/config/:tab" element={<MilestoneConfig />} />
            <Route path="/calendar-config" element={<Navigate to={getModuleLastPath('calendar-config')} replace />} />
            <Route path="/calendar-config/:tab" element={<CalendarConfig />} />
            <Route path="/project-monitor" element={<Navigate to="/dashboard/monitor" replace />} />
            <Route path="/callback" element={<Callback />} />
          </Routes>
        </div>
      </Content>
    </Layout>
  );
};

const App: FC = () => {
  return (
    <Router basename={basename}>
      <AuthGate>
        <AppLayout />
      </AuthGate>
    </Router>
  );
};

export default App;
